import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import type { Pagination, SortItem } from "./commonApiTypes";
import type { Display } from "./DisplayService";
import type { ContentBlock } from "./ContentBlockService";
import { User } from "./UserService";
import { ApiService } from "./ApiService";

// GraphQL queries
const DISPLAY_CONTENT_ASSIGNMENT_LIST_QUERY = gql`
  query DisplayContentAssignmentList(
    $filter: DisplayContentAssignmentFilter
    $params: PaginationAndSorting
  ) {
    displayContentAssignmentList(filter: $filter, params: $params) {
      code
      data {
        id
        displayId
        contentBlockId
        durationSeconds
        orderNo
        startDatetime
        endDatetime
        createdAt
        createdBy
        userCreated {
          id
        }
        display {
          id
        }
        contentBlock {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_CONTENT_ASSIGNMENT_GET_QUERY = gql`
  query DisplayContentAssignmentGet($id: ID) {
    displayContentAssignmentGet(id: $id) {
      code
      data {
        id
        displayId
        contentBlockId
        durationSeconds
        orderNo
        startDatetime
        endDatetime
        createdAt
        createdBy
        userCreated {
          id
        }
        display {
          id
        }
        contentBlock {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_CONTENT_ASSIGNMENT_GET_MANY_QUERY = gql`
  query DisplayContentAssignmentGetMany($ids: [ID]) {
    displayContentAssignmentGetMany(ids: $ids) {
      code
      data {
        id
        displayId
        contentBlockId
        durationSeconds
        orderNo
        startDatetime
        endDatetime
        createdAt
        createdBy
        userCreated {
          id
        }
        display {
          id
        }
        contentBlock {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const DISPLAY_CONTENT_ASSIGNMENT_CREATE_MUTATION = gql`
  mutation DisplayContentAssignmentCreate(
    $params: DisplayContentAssignmentInput
  ) {
    displayContentAssignmentCreate(params: $params) {
      code
      data {
        id
        displayId
        contentBlockId
        durationSeconds
        orderNo
        startDatetime
        endDatetime
        createdAt
        createdBy
        userCreated {
          id
        }
        display {
          id
        }
        contentBlock {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_CONTENT_ASSIGNMENT_UPDATE_MUTATION = gql`
  mutation DisplayContentAssignmentUpdate(
    $where: DisplayContentAssignmentWhereInput
    $params: DisplayContentAssignmentInput
  ) {
    displayContentAssignmentUpdate(where: $where, params: $params) {
      code
      data {
        id
        displayId
        contentBlockId
        durationSeconds
        orderNo
        startDatetime
        endDatetime
        createdAt
        createdBy
        userCreated {
          id
        }
        display {
          id
        }
        contentBlock {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_CONTENT_ASSIGNMENT_REMOVE_MUTATION = gql`
  mutation DisplayContentAssignmentRemove(
    $where: DisplayContentAssignmentWhereInput
  ) {
    displayContentAssignmentRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_CONTENT_ASSIGNMENT_REMOVE_MANY_MUTATION = gql`
  mutation DisplayContentAssignmentRemoveMany(
    $where: [DisplayContentAssignmentWhereInput]
  ) {
    displayContentAssignmentRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types

export interface DisplayContentAssignment {
  id: string;
  displayId?: string;
  contentBlockId?: string;
  durationSeconds?: number;
  orderNo?: number;
  startDatetime?: string;
  endDatetime?: string;
  createdAt?: string;
  createdBy?: string;
  userCreated?: User;
  display?: Display;
  contentBlock?: ContentBlock;
}

export interface DisplayContentAssignmentFilter {
  id?: string[];
  displayId?: string[];
  contentBlockId?: string[];
  startDatetime?: string;
  endDatetime?: string;
}

export interface DisplayContentAssignmentInput {
  displayId?: string | null | undefined;
  contentBlockId?: string | null | undefined;
  durationSeconds?: number | null | undefined;
  orderNo?: number | null | undefined;
  startDatetime?: string | null | undefined;
  endDatetime?: string | null | undefined;
}

export interface DisplayContentAssignmentWhereInput {
  id: string;
}

/**
 * Display Content Assignment service that handles all display content assignment-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class DisplayContentAssignmentService {
  /**
   * Get a list of display content assignments with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: DisplayContentAssignmentFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      DISPLAY_CONTENT_ASSIGNMENT_LIST_QUERY,
      {
        filter: filter || {},
        params: {
          pagination: pagination || undefined,
          sortBy: sortBy || undefined,
        },
      }
    );
    return result;
  }

  /**
   * Get a single display content assignment by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      DISPLAY_CONTENT_ASSIGNMENT_GET_QUERY,
      {
        id: id,
      }
    );
    return result;
  }

  /**
   * Get multiple display content assignments by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      DISPLAY_CONTENT_ASSIGNMENT_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Create a new display content assignment
   */
  static async create(
    params: DisplayContentAssignmentInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAY_CONTENT_ASSIGNMENT_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing display content assignment
   */
  static async update(
    id: string,
    params: DisplayContentAssignmentInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAY_CONTENT_ASSIGNMENT_UPDATE_MUTATION,
      {
        where: { id },
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a display content assignment
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAY_CONTENT_ASSIGNMENT_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple display content assignments
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      DISPLAY_CONTENT_ASSIGNMENT_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
